import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Paper, Alert, CircularProgress } from '@mui/material';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Rediriger si déjà connecté et est admin, mais seulement au chargement du composant
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (currentUser) {
        try {
          // Vérifier si l'utilisateur est un administrateur ou PDG
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists() && (userDoc.data().role === 'admin' || userDoc.data().role === 'PDG')) {
            navigate('/admin-dashboard');
          }
          // Si l'utilisateur n'est pas admin, on reste sur la page de login
        } catch (error) {
          console.error('Erreur lors de la vérification du statut admin:', error);
        }
      }
    };
    
    checkAdminStatus();
  }, [currentUser, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Connexion avec Firebase Authentication
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Vérifier si l'utilisateur est un administrateur ou PDG
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists() && (userDoc.data().role === 'admin' || userDoc.data().role === 'PDG')) {
        navigate('/admin-dashboard');
      } else {
        // Si l'utilisateur n'est pas un administrateur ou PDG, déconnexion
        await auth.signOut();
        setError('Accès non autorisé. Vous n\'êtes pas administrateur.');
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
      setError('Identifiants incorrects. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '80vh',
      backgroundColor: '#f5f5f5'
    }}>
      <Paper elevation={3} sx={{ 
        p: 4, 
        width: '100%', 
        maxWidth: '400px',
        borderRadius: '10px'
      }}>
        <Typography variant="h5" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold', color: '#014527' }}>
          Connexion Administrateur
        </Typography>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          
          <TextField
            label="Mot de passe"
            type="password"
            fullWidth
            margin="normal"
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            sx={{ mb: 3 }}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ 
              py: 1.5, 
              backgroundColor: '#014527',
              '&:hover': {
                backgroundColor: '#01331d',
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Se connecter'}
          </Button>
        </form>
        
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2">
            Pas encore administrateur ? <Button 
              component={Link} 
              to="/admin-register" 
              sx={{ 
                p: 0, 
                textTransform: 'none', 
                color: '#014527',
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: 'transparent'
                } 
              }}
            >
              Demander un accès
            </Button>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminLogin;